import React from 'react';
import { useTranslation } from 'react-i18next';
import SEO from './SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import './LegalPageLayout.css';

/**
 * Shared layout for Terms/Privacy/Returns/Shipping — four near-identical
 * long-form policy pages, each just a numbered list of {title, body}
 * i18n sections under its own namespace. `extra` renders after the
 * sections for page-specific dynamic content (e.g. Returns' live
 * returns-window notice, Shipping's live delivery estimate); `after`
 * renders below them (the Cookie Policy's list of cookies).
 */
const LegalPageLayout = ({ namespace, sectionCount, extra, after }) => {
  const { t } = useTranslation();
  const { storeName, storeEmail, currency, storeDefaultCountry, storeLegalName, storeNipt, storeAddress } = useSiteSettings();

  const interpolation = {
    storeName,
    storeEmail,
    currency,
    country: storeDefaultCountry,
  };

  return (
    <div className="legal-page">
      <SEO
        title={t(`${namespace}.title`)}
        description={t(`${namespace}.lead`, interpolation)}
      />

      <header className="legal-hero">
        <span className="legal-eyebrow">{t(`${namespace}.eyebrow`, 'Legal')}</span>
        <h1 className="legal-title">{t(`${namespace}.title`)}</h1>
        <p className="legal-lead">{t(`${namespace}.lead`, interpolation)}</p>
      </header>

      {extra}

      <div className="legal-body">
        {Array.from({ length: sectionCount }, (_, i) => i + 1).map((n) => (
          <section className="legal-section" key={n}>
            <h2>{t(`${namespace}.section${n}.title`)}</h2>
            <p>{t(`${namespace}.section${n}.body`, interpolation)}</p>
          </section>
        ))}
      </div>

      {after}

      {(storeLegalName || storeNipt) && (
        <section className="legal-section legal-seller">
          <h2>{t('legal.seller.title', 'Seller')}</h2>
          <p>
            {storeLegalName && storeLegalName !== storeName
              ? t('legal.seller.brandOf', `${storeName} is a brand of ${storeLegalName}.`, { storeName, company: storeLegalName })
              : (storeLegalName || storeName)}
            {storeNipt && <> {t('legal.seller.nipt', `NIPT ${storeNipt}.`, { nipt: storeNipt })}</>}
            {storeAddress && <> {t('legal.seller.address', `Registered address: ${storeAddress}.`, { address: storeAddress })}</>}
          </p>
        </section>
      )}
    </div>
  );
};

export default LegalPageLayout;
